import React, { useState } from "react";
import { toast } from "react-toastify";
import "./index.css";

const FUNCTIONS_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/red-platform/us-central1"
    : "https://us-central1-red-platform.cloudfunctions.net";

const FunctionHealth = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [errorsOnly, setErrorsOnly] = useState(false);

  const runDigest = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await fetch(`${FUNCTIONS_BASE_URL}/triggerFunctionHealthDigest`, {
        method: "POST",
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || "Function returned an error");
        toast.error("Health digest failed");
      } else {
        setResult(data);
        if (data.emailSent) {
          toast.success(`Digest emailed to ${data.recipient}`);
        } else {
          toast.warn("Digest computed but email failed — see results below");
        }
      }
    } catch (err) {
      console.error("FunctionHealth: request failed", err);
      setError(err.message || "Request failed");
      toast.error("Could not reach the function");
    }
    setRunning(false);
  };

  const renderResults = () => {
    if (!result) return null;

    const sortedFns = Object.entries(result.perFunction || {}).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    });

    return (
      <div className="function-health__results">
        <div className="function-health__summary">
          <div className="function-health__summary-item">
            <div className="function-health__summary-label">Total errors</div>
            <div
              className={`function-health__summary-value ${
                result.totalErrors === 0
                  ? "function-health__summary-value--ok"
                  : result.totalErrors < 25
                  ? "function-health__summary-value--warn"
                  : "function-health__summary-value--bad"
              }`}
            >
              {result.totalErrors}
            </div>
          </div>
          <div className="function-health__summary-item">
            <div className="function-health__summary-label">
              Erroring / deployed
            </div>
            <div className="function-health__summary-value">
              {result.erroringFns} / {result.totalDeployed}
            </div>
          </div>
          <div className="function-health__summary-item">
            <div className="function-health__summary-label">Email status</div>
            <div
              className={`function-health__summary-value ${
                result.emailSent
                  ? "function-health__summary-value--ok"
                  : "function-health__summary-value--bad"
              }`}
            >
              {result.emailSent ? "Sent" : "Failed"}
            </div>
          </div>
        </div>

        {!result.emailSent && result.emailError && (
          <div className="function-health__email-error">
            <strong>Email error:</strong> {result.emailError}
          </div>
        )}

        {sortedFns.length === 0 ? (
          <div className="function-health__empty">
            No deployed functions found.
          </div>
        ) : (
          <>
            <label className="function-health__toggle">
              <input
                type="checkbox"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
              />
              Show only functions with errors
            </label>
            <div className="function-health__table-container">
              <table className="function-health__table">
                <thead>
                  <tr>
                    <th>Function</th>
                    <th>Errors</th>
                    <th>Sample message</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFns
                    .filter(([, count]) => !errorsOnly || count > 0)
                    .map(([fn, count], i) => (
                      <tr
                        key={fn}
                        className={i % 2 === 1 ? "function-health__row--alt" : ""}
                      >
                        <td className="function-health__fn-name">{fn}</td>
                        <td
                          className={`function-health__count ${
                            count === 0
                              ? "function-health__count--ok"
                              : count >= 10
                              ? "function-health__count--bad"
                              : "function-health__count--warn"
                          }`}
                        >
                          {count}
                        </td>
                        <td className="function-health__sample">
                          {(result.sampleByFunction || {})[fn] || "—"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="function-health">
      <div className="function-health__header">
        <div>
          <h2 className="function-health__title">Function health</h2>
          <p className="function-health__subtitle">
            Scans the last 24 hours of Cloud Logging for errors across all
            Firebase Functions and emails a digest to the ops recipient.
          </p>
        </div>
        <button
          className="function-health__run-btn"
          onClick={runDigest}
          disabled={running}
        >
          {running ? "Running..." : "Run digest now"}
        </button>
      </div>

      {error && (
        <div className="function-health__error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {renderResults()}
    </div>
  );
};

export default FunctionHealth;
